/**
 * Shared display helpers for artifact chunk/entity counts and ingest status.
 * Prefer meta.processing_status (latest ingest job) over raw DB aggregates,
 * which can include duplicate rows from re-ingest passes.
 */

import type { Artifact } from '../types/api';

export interface ArtifactProcessingStatus {
  status?: 'completed' | 'failed' | 'partial' | string;
  parse_success?: boolean;
  chunk_success?: boolean;
  entity_extraction_success?: boolean | null;
  chunks_created?: number;
  entities_created?: number;
  error?: string | null;
  last_processed_at?: string;
}

export function getArtifactProcessingStatus(
  artifact: Artifact,
): ArtifactProcessingStatus | undefined {
  return artifact.meta?.processing_status as ArtifactProcessingStatus | undefined;
}

export function computeOverallProcessingStatus(
  status: ArtifactProcessingStatus,
): 'completed' | 'failed' | 'partial' {
  if (status.parse_success && status.chunk_success) {
    if (status.entity_extraction_success === true) {
      return 'completed';
    }
    if (status.entity_extraction_success === false) {
      return 'partial';
    }
    return 'completed';
  }
  if (status.parse_success) {
    return 'partial';
  }
  return 'failed';
}

/** Ingest badge label — processing_status wins over stale ingest_status column. */
export function getIngestStatusLabel(artifact: Artifact): string | null {
  const processing = getArtifactProcessingStatus(artifact);
  if (processing?.status) {
    const overall = computeOverallProcessingStatus(processing);
    if (overall === 'completed') return 'ingested';
    if (overall === 'failed') return 'failed';
    if (overall === 'partial') return 'partial';
    return processing.status;
  }
  return artifact.ingest_status ?? null;
}

export function getIngestStatusBadgeClass(label: string | null): string {
  switch (label) {
    case 'ingested':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'partial':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return '';
  }
}

/** Chunk count for list headers — matches Processing Status card when available. */
export function getDisplayChunkCount(
  artifact: Artifact,
  dbCounts: Record<string, number>,
): number {
  const processing = getArtifactProcessingStatus(artifact);
  if (processing && typeof processing.chunks_created === 'number') {
    return processing.chunks_created;
  }
  return dbCounts[artifact.id] ?? 0;
}

/** Entity count for list headers — matches Processing Status card when available. */
export function getDisplayEntityCount(
  artifact: Artifact,
  dbCounts: Record<string, number>,
): number {
  const processing = getArtifactProcessingStatus(artifact);
  if (processing && typeof processing.entities_created === 'number') {
    return processing.entities_created;
  }
  return dbCounts[artifact.id] ?? 0;
}

/** True when /artifacts/stats response predates chunk aggregate fields. */
export function isArtifactStatsStale(
  apiStats: { total_artifacts?: number; artifacts_with_chunks?: number } | undefined,
): boolean {
  return (
    apiStats != null &&
    (apiStats.total_artifacts ?? 0) > 0 &&
    apiStats.artifacts_with_chunks === undefined
  );
}
